import { RoleContextDTO } from '../dtos';
import { useContextStore } from '../../../stores/contextStore';

const STORAGE_KEY = 'lecturo_current_role_context';

/**
 * Sync role context data to the main context store
 * This ensures API calls have the correct context headers AND
 * that components like Reports get the right tenant/subject context
 */
const syncRoleContextToContextStore = (context: RoleContextDTO): void => {
  try {
    const contextStore = useContextStore.getState();

    // CRITICAL: Set the complete context in the main store
    // This is essential for Reports page and other components that depend on context
    contextStore.setActiveContext({
      tenantId: context.tenantId,
      domainId: context.domainId || null,
      subjectId: context.subjectId || null,
    });

    // Verify the context was set correctly
    const updatedState = useContextStore.getState();

    if (!updatedState.activeTenantId && context.tenantId) {
      console.error(
        'CRITICAL: Tenant context sync failed! This will cause "No tenant selected" errors'
      );
    }
  } catch (error) {
    console.error('CRITICAL: Failed to sync role context to context store:', error);
  }
};

/**
 * Client-side storage utility for role context
 * Uses sessionStorage to persist only during the browser session
 */
export const roleContextStorage = {
  // Get current role context from storage
  getCurrentRoleContext(): RoleContextDTO | null {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (!stored) return null;

      const parsed = JSON.parse(stored);
      return parsed;
    } catch (error) {
      console.error('Failed to parse role context from storage:', error);
      roleContextStorage.clearCurrentRoleContext();
      return null;
    }
  },

  // Set current role context in storage
  setCurrentRoleContext(context: RoleContextDTO): void {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(context));

      // Sync to main context store for API calls
      syncRoleContextToContextStore(context);
    } catch (error) {
      console.error('Failed to store role context:', error);
    }
  },

  // Clear current role context from storage
  clearCurrentRoleContext(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEY);

      // Also clear the main context store
      const contextStore = useContextStore.getState();
      contextStore.clearActiveContext();
    } catch (error) {
      console.error('Failed to clear role context from storage:', error);
    }
  },

  // Get a user-specific storage key (if we need user-specific contexts)
  getUserStorageKey(userId: string): string {
    return `${STORAGE_KEY}_${userId}`;
  },
};
